import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import ReactMarkdown from 'react-markdown';
import SendIcon from '@mui/icons-material/Send';
import StandardTextfield from "../standardTextfield";
import showdown from 'showdown';

export default function Step2({ onNext, chatMessages, setChatMessages, isTypingAncestor }) {
  const [innovations, setInnovations] = useState([]);
  const [message, setMessage] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typingMessage, setTypingMessage] = useState(""); // Streaming message
  const [serverReplied, setServerReplied] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false); // Usato per rimuovere i messaggi
  const fullMessage = useRef(""); // Keep track of the entire message
  const token = localStorage.getItem("token");
  const messagesEndRef = useRef(null);

  const converter = new showdown.Converter();

  // Function to fetch startups from the server
  const fetchInnovations = async () => {
    try {
      setIsTyping(true); // Show typing effect during loading
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/advisory/steps/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const stepData = response.data;
      setInnovations(stepData.innovations); // Set the received startups

      // Add the innovations message to chatMessages
      if (stepData.innovations && stepData.innovations.length > 0) {
        setChatMessages(prevMessages => [
          ...prevMessages,
          {
            id: prevMessages.length,
            type: 'innovations',
            innovations: stepData.innovations,
            sender: 'AI Navigator'
          }
        ]);
      }

      // Add the Yes/No question as a message
      setChatMessages(prevMessages => [
        ...prevMessages,
        {
          id: prevMessages.length + 1,
          type: 'options',
          sender: 'AI Navigator'
        }
      ]);

    } catch (error) {
      setError("Error fetching startups");
    } finally {
      setIsTyping(false); // Hide typing effect after loading
    }
  };

  useEffect(() => {
    fetchInnovations();
  }, []);

  // Scroll to bottom when chatMessages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (message) {
      onNext(3, innovations, message);
    }
  }, [message]);

  // Function to handle new message input
  const handleNewMessage = (event) => {
    setNewMessage(event.target.value);
  };

  function replace_bold_names_with_links(response) {
    const pattern = /\*\*(.*?)\*\* \(ID: (.*?)\)/g;
  
    // Replace each found bold text with an HTML link with target="_blank" if it's a startup name
    return response.replace(pattern, (match, name, sid) => {
        if (name && sid) {
            const url = `${process.env.REACT_APP_BASE_URL}/startups/startup/${sid}`;
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${name}</a>`;
        }
        return match;  // Keep the original text if not a startup name
    });
  }

  const handleAddMessage = async () => {
    if (newMessage.trim() !== "") {
        setChatMessages(prevMessages => [
            ...prevMessages,
            { id: prevMessages.length, text: newMessage, sender: "You" }
        ]);

        setIsTyping(true);
        setTypingMessage("Typing...");

        try {
            const payload = {
                message: newMessage,
                stream: true
            };

            fullMessage.current = "";

            const response = await fetch(`${process.env.REACT_APP_API_URL}/advisory/message?stream=true`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream not supported or response body is empty.');
            }

            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            let accumulatedText = "";

            while (true) {
                const { value, done } = await reader.read();

                if (done || accumulatedText.includes("[DONE]")) {
                    break;
                }

                value.replaceAll("\n\n\n\n", '__DD__\n\n').split("\n\n").forEach((element) => {
                    if (element !== "") {
                        const processedElement = element.replaceAll("__DD__", '\n\n');
                        if (processedElement.startsWith("data: ")) {
                            accumulatedText += processedElement.substring(6);
                        } else {
                            accumulatedText += processedElement;
                        }
                    }
                });

                const formattedText = converter.makeHtml(accumulatedText.replaceAll("\n   -", "\n    -"));
                setTypingMessage(formattedText);
            }

            // Parsa e pulisci in caso
            fullMessage.current = accumulatedText
            .replace(/data:  -/g, '')    
            .replace(/data:    -/g, '')
            .replace(/data: -/g, '')
            .replace(/\[DONE\]/g, '')
            .trim();

            fullMessage.current = replace_bold_names_with_links(fullMessage.current);

            // Update chat messages
            setChatMessages(prevMessages => [
            ...prevMessages,
            { id: prevMessages.length, text: fullMessage.current, sender: "AI Navigator" },
            { id: prevMessages.length + 1, type: "button", sender: "AI Navigator" }
            ]);

            setTypingMessage("");
            setIsTyping(false);

        } catch (error) {
            console.error('Error during fetch:', error);
            setError('Error sending message');
            setIsTyping(false);
        }

        setNewMessage("");
    }
  };

  // Function to handle back button click
  const handleBackToStep1 = async () => {
    try {
      setIsTyping(true); // Disable input while processing
      onNext(1);
    } catch (error) {
      window.location.reload();
    } finally {
      setIsTyping(false); // Re-enable input
    }
  };

  // Remove messages of type 'innovations' and 'options' when the buttons are clicked
  const removePreviousMessages = () => {
    setChatMessages(prevMessages =>
      prevMessages.filter(m => m.type !== 'innovations' && m.type !== 'options')
    );
  };

  const handleFirstYesClick = async () => {
    removePreviousMessages(); // Eliminate the previous messages
    setIsTyping(false);
    setMessage(true);
  };

  const handleFirstNoClick = () => {
    removePreviousMessages(); // Eliminate the previous messages
    onNext(3, innovations, false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevents adding a new line
      handleAddMessage();     // Calls your message sending function
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Chat Container */}
      <div className="flex flex-col flex-grow h-0">
        {/* Chat Messages Area */}
        <div className="flex flex-col flex-grow overflow-y-auto px-5">
          {chatMessages.map((m, index) => {
            if (m.type === 'innovations') {
              // Render innovations message
              return (
                <div key={`${m.sender}-${index}`} className="p-2 bg-gray-800 w-full rounded-2xl max-w-[80%] whitespace-pre-wrap mt-4">
                  <span className="text-white font-semibold">
                    The following startups are available:
                  </span>
                  <ul className="list-disc list-inside mt-2">
                    {m.innovations.map((innovation) => (
                      <li key={innovation.id} className="text-white">
                        {innovation.name}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            } else if (m.type === 'options') {
              
              return (
                <div key={`${m.sender}-${index}`} className="p-5 flex flex-col items-center bg-black-l rounded-2xl max-w-[90%] mt-4" style={{ marginLeft: '5%' }}>
                  <span className="font-semibold text-white">
                    Is this the first time you take part in the evaluation process? Do you want me to explain how it works?
                  </span>
                  <div className="flex gap-2 mt-4">
                    <Button variant="contained" sx={{ backgroundColor: "rgb(var(--global-color-primary))", color: "#000" }} onClick={handleFirstYesClick}>
                      Yes
                    </Button>
                    <Button variant="outlined" sx={{ borderColor: "rgb(var(--global-color-primary))", color: "#fff" }} onClick={handleFirstNoClick}>
                      No
                    </Button>
                  </div>
                </div>
              );
            } else {
              // Render normal messages
              return (
                <div
                  key={`${m.sender}-${index}`}
                  className={`flex flex-col gap-1 ${
                    m.sender === "You" ? "items-end" : "items-start"
                  }`}
                >
                  <span className="font-semibold">{m.sender}</span>
                  <div className="p-2 bg-gray-800 w-full rounded-2xl max-w-[80%] whitespace-pre-wrap msgClass">
                    {m.sender === "AI Navigator" ? (
                      <ReactMarkdown>{m.text}</ReactMarkdown>
                    ) : (
                      <span>{m.text}</span>
                    )}
                  </div>
                </div>
              );
            }
          })}

          {/* Typing Indicator */}
          {isTyping && typingMessage && (
            <div className="flex flex-col gap-1 items-start mt-4">
              <span className="font-semibold">AI Navigator</span>
              <div
                className="p-2 bg-gray-800 rounded-2xl max-w-[80%] flex flex-col msgClass"
                dangerouslySetInnerHTML={{ __html: typingMessage }}
              />
            </div>
          )}

          {/* Visualizza l'indicatore "Typing..." se isTypingAncestor è true */}
          {isTypingAncestor && (
            <div className="flex flex-col gap-1 items-start">
              <span className="font-semibold">AI Navigator</span>
              <div className="p-2 bg-gray-800 rounded-2xl max-w-[80%] flex flex-col msgClass">
                <span>Typing...</span> {/* Messaggio Typing */}
              </div>
            </div>
          )}

          {/* Scroll Anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-5 w-full bg-black flex items-center gap-2">
          {/* Back to Step 1 Button */}
          <Button
            sx={{
              backgroundColor: "rgb(var(--global-color-primary))",
              color: "#000 !important",
              '&:hover': {
                backgroundColor: "rgb(var(--global-color-primary))",
              }
            }}
            onClick={handleBackToStep1}
            disabled={isTyping}
          >
            Restart
          </Button>

          <StandardTextfield
            variant="outlined"
            multiline={true}
            value={newMessage}
            placeholder="Input here"
            onChange={handleNewMessage}
            disabled={isTyping}
            style={{ flexGrow: 1 }}
          />

          <Button
            variant="contained"
            sx={{
              backgroundColor: "rgb(var(--global-color-primary))",
              color: "black",
              minWidth: '48px',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: "rgb(var(--global-color-primary))",
              }
            }}
            onClick={handleAddMessage}
            disabled={isTyping || (!buttonClicked)}
          >
            <SendIcon />
          </Button>

        </div>
      </div>

      {/* Display error */}
      {error && <div style={{ color: "red" }}>{error}</div>}
    </div>
  );
}
